/**
 * updates.js
 * 
 * This module defines Recoil selectors that handle state updates for the SightFlight Designer application.
 * These selectors provide controlled methods to modify the application state while maintaining
 * immutability principles. Each selector focuses on a specific update operation such as:
 * 
 * - Adding, updating, and deleting obstacles
 * - Adding and deleting solar panels
 * - Deleting segments
 * - Updating polygon details and setbacks
 * 
 * The selectors follow a common pattern of immutably updating nested state objects
 * through careful copying and modification.
 */

import {polygonsState, selectedIndicesState} from "./states";
import {selector} from "recoil";
import {indexToLabel} from "../Segments/Labels";

/**
 * Selector to add a new obstacle to a segment
 * 
 * This selector adds a new obstacle object to the obstructions array
 * of a specified segment in the polygons state.
 */
export const addObstaclesSelector = selector({
    key: 'addObstaclesSelector',
    get: ({ get }) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({ set, get }, { newObstacle, segmentIndex }) => {
        // Update the polygons state
        set(polygonsState, (oldPolygons) => {
            // Validate that the segment index is within bounds
            if (segmentIndex >= 0 && segmentIndex < oldPolygons.length) {
                // Create a copy of the polygons array to maintain immutability
                const updatedPolygons = [...oldPolygons];
                // Create a copy of the target segment
                const segment = {...updatedPolygons[segmentIndex]};

                // Validate that the new obstacle is a valid object
                if (typeof newObstacle === 'object' && newObstacle !== null) {
                    // Add the new obstacle to the obstructions array
                    segment.obstructions = [...segment.obstructions, newObstacle];
                } else {
                    console.error('newObstacle is not an object');
                    return oldPolygons; // Return unchanged state on error
                }

                // Update the segment in the polygons array
                updatedPolygons[segmentIndex] = segment;
                return updatedPolygons;
            } else {
                console.error('Invalid segmentIndex');
                return oldPolygons; // Return unchanged state on error
            }
        });
    }
});

/**
 * Selector to delete an obstacle from a segment
 * 
 * This selector removes an obstacle at the specified index from the
 * obstructions array of the currently selected segment.
 */
export const deleteObstacleSelector = selector({
    key: 'deleteObstacleSelector',
    get: ({ get }) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({ set, get }, obsIndexToDelete) => {
        // Get the currently selected segment index
        const { segmentIndex } = get(selectedIndicesState);
        
        // Update the polygons state
        set(polygonsState, (oldPolygons) => {
            // Validate that the segment index is within bounds
            if (segmentIndex >= 0 && segmentIndex < oldPolygons.length) {
                // Create a copy of the polygons array to maintain immutability
                const updatedPolygons = [...oldPolygons];
                // Create a copy of the target segment
                const segment = {...updatedPolygons[segmentIndex]};

                // Validate that the obstacle index is within bounds
                if (obsIndexToDelete >= 0 && obsIndexToDelete < segment.obstructions.length) {
                    // Filter out the obstacle to delete
                    segment.obstructions = segment.obstructions.filter((_, index) => index !== obsIndexToDelete);
                } else {
                    console.error('Invalid obstacle index to delete');
                    return oldPolygons; // Return unchanged state on error
                }

                // Update the segment in the polygons array
                updatedPolygons[segmentIndex] = segment;
                return updatedPolygons;
            } else {
                console.error('Invalid segmentIndex');
                return oldPolygons; // Return unchanged state on error
            }
        });
    }
});

/**
 * Selector to update the height of an obstacle
 * 
 * This selector modifies the height property of a specific obstacle
 * in the currently selected segment.
 */
export const updateObstacleHeightSelector = selector({
    key: 'updateObstacleHeightSelector',
    get: ({get}) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({ set, get }, newHeight) => {
        // Get the currently selected segment and obstacle indices
        const { segmentIndex, obstacleIndex } = get(selectedIndicesState);

        // Only proceed if both segment and obstacle are selected
        if (segmentIndex !== null && obstacleIndex !== null) {
            set(polygonsState, (oldPolygons) => {
                // Create a shallow copy of the polygons array
                const updatedPolygons = oldPolygons.slice();

                // Validate that the segment index is within bounds
                if (segmentIndex >= 0 && segmentIndex < updatedPolygons.length) {
                    // Create a shallow copy of the segment object
                    const segment = { ...updatedPolygons[segmentIndex] };

                    // Validate that the obstacle index is within bounds
                    if (obstacleIndex >= 0 && obstacleIndex < segment.obstructions.length) {
                        // Create a shallow copy of the obstructions array
                        segment.obstructions = segment.obstructions.slice();
                        
                        // Update the height of the selected obstacle
                        segment.obstructions[obstacleIndex] = { 
                            ...segment.obstructions[obstacleIndex], 
                            height: newHeight 
                        };
                        
                        // Update the segment in the polygons array
                        updatedPolygons[segmentIndex] = segment;
                    } else {
                        console.error('Invalid obstacle index to update height');
                    }
                } else {
                    console.error('Invalid segmentIndex');
                }

                return updatedPolygons;
            });
        } else {
            console.error('Invalid segmentIndex or obstacleIndex');
        }
    },
});

/**
 * Selector to update the offset of an obstacle
 * 
 * This selector modifies the offset property of a specific obstacle
 * in the currently selected segment.
 */
export const updateObstacleOffsetSelector = selector({
    key: 'updateObstacleOffsetSelector',
    get: ({get}) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({ set, get }, newOffset) => {
        // Get the currently selected segment and obstacle indices
        const { segmentIndex, obstacleIndex } = get(selectedIndicesState);

        // Only proceed if both segment and obstacle are selected
        if (segmentIndex !== null && obstacleIndex !== null) {
            set(polygonsState, (oldPolygons) => {
                // Create a shallow copy of the polygons array
                const updatedPolygons = oldPolygons.slice();

                // Validate that the segment index is within bounds
                if (segmentIndex >= 0 && segmentIndex < updatedPolygons.length) {
                    // Create a shallow copy of the segment object
                    const segment = { ...updatedPolygons[segmentIndex] };

                    // Validate that the obstacle index is within bounds
                    if (obstacleIndex >= 0 && obstacleIndex < segment.obstructions.length) {
                        // Create a shallow copy of the obstructions array
                        segment.obstructions = segment.obstructions.slice();
                        
                        // Update the offset of the selected obstacle
                        segment.obstructions[obstacleIndex] = { 
                            ...segment.obstructions[obstacleIndex], 
                            offset: newOffset 
                        };
                        
                        // Update the segment in the polygons array
                        updatedPolygons[segmentIndex] = segment;
                    } else {
                        console.error('Invalid obstacle index to update offset');
                    }
                } else {
                    console.error('Invalid segmentIndex');
                }

                return updatedPolygons;
            });
        } else {
            console.error('Invalid segmentIndex or obstacleIndex');
        }
    },
});

/**
 * Selector to add a new solar panel to a segment
 * 
 * This selector adds a new panel object to the panels array
 * of a specified segment in the polygons state.
 */
export const addPanelSelector = selector({
    key: 'addPanelSelector',
    get: ({ get }) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({ set, get }, { newPanel, segmentIndex }) => {
        set(polygonsState, (oldPolygons) => {
            // Validate that the segment index is within bounds
            if (segmentIndex >= 0 && segmentIndex < oldPolygons.length) {
                // Create a copy of the polygons array to maintain immutability
                const updatedPolygons = [...oldPolygons];
                // Create a copy of the target segment
                const segment = {...updatedPolygons[segmentIndex]};

                // Validate that the new panel is a valid object
                if (typeof newPanel === 'object' && newPanel !== null) {
                    // Add the new panel to the panels array
                    segment.panels = [...segment.panels, newPanel];
                } else {
                    console.error('newPanel is not an object');
                    return oldPolygons; // Return unchanged state on error
                }

                // Update the segment in the polygons array
                updatedPolygons[segmentIndex] = segment;
                return updatedPolygons;
            } else {
                console.error('Invalid segmentIndex');
                return oldPolygons; // Return unchanged state on error
            }
        });
    }
});

/**
 * Selector to delete multiple solar panels from a segment
 * 
 * This selector removes panels at the specified indices from the
 * panels array of the currently selected segment.
 */
export const deletePanelsSelector = selector({
    key: 'deletePanelsSelector',
    get: ({ get }) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({ set, get }, panelIndices) => {
        // Get the currently selected segment index
        const { segmentIndex } = get(selectedIndicesState);
        
        // Update the polygons state
        set(polygonsState, (oldPolygons) => {
            // Validate that the segment index is within bounds
            if (segmentIndex >= 0 && segmentIndex < oldPolygons.length) {
                // Create a copy of the polygons array to maintain immutability
                const updatedPolygons = [...oldPolygons];
                // Create a copy of the target segment
                const segment = {...updatedPolygons[segmentIndex]};

                // Validate that all panel indices are within bounds
                for (let indexToDelete of panelIndices) {
                    if (indexToDelete < 0 || indexToDelete >= segment.panels.length) {
                        console.error('Invalid panel index to delete');
                        return oldPolygons; // Return unchanged state on error
                    }
                }
                
                // Filter out the panels to delete
                segment.panels = segment.panels.filter((_, index) => !panelIndices.includes(index));

                // Update the segment in the polygons array
                updatedPolygons[segmentIndex] = segment;
                return updatedPolygons;
            } else {
                console.error('Invalid segmentIndex');
                return oldPolygons; // Return unchanged state on error
            }
        });
    }
});

/**
 * Selector to delete a segment from the polygons array
 * 
 * This selector removes a segment at the specified index and
 * updates the labels of subsequent segments to maintain continuity.
 */
export const deleteSegmentSelector = selector({
    key: 'deleteSegmentSelector',
    get: ({ get }) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({ set, get }, indexToDelete) => {
        set(polygonsState, (oldPolygons) => {
            // Create a copy of the polygons array to maintain immutability
            const updatedPolygons = [...oldPolygons];
            
            // Validate that the segment index is within bounds
            if (indexToDelete >= 0 && indexToDelete < updatedPolygons.length) {
                // Remove the segment at the specified index
                updatedPolygons.splice(indexToDelete, 1);
                
                // Update the labels of subsequent segments
                return updatedPolygons.map((segment, index) => {
                    if (index >= indexToDelete) {
                        return {
                            ...segment,
                            label: indexToLabel(index), // Update the label based on the new index
                        };
                    } else {
                        return segment;
                    }
                });
            } else {
                console.error('Invalid index to delete');
                return oldPolygons; // Return unchanged state on error
            }
        });
    },
});

/**
 * Selector to update the details of a polygon segment
 * 
 * This selector replaces the details object of the currently
 * selected segment with the provided updated details.
 */
export const updatePolygonDetailsSelector = selector({
    key: 'updatePolygonDetailsSelector',
    get: ({get}) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({ set, get }, updatedDetails ) => {
        // Get the currently selected segment index
        const { segmentIndex } = get(selectedIndicesState);
        
        // Only proceed if a segment is selected
        if (segmentIndex !== null && segmentIndex !== undefined) {
            set(polygonsState, (oldPolygons) => {
                // Create a copy of the polygons array to maintain immutability
                const updatedPolygons = [...oldPolygons];

                // Update the details of the selected segment
                updatedPolygons[segmentIndex] = {
                    ...updatedPolygons[segmentIndex],
                    details: updatedDetails
                };

                return updatedPolygons;
            });
        } else {
            console.error('Invalid segmentIndex');
        }
    },
});

/**
 * Selector to update the setbacks of a polygon segment
 * 
 * This selector replaces the setbacks object of the specified
 * segment with the provided updated setbacks.
 */
export const updatePolygonSetbacksSelector = selector({
    key: 'updatePolygonSetbacksSelector',
    get: ({get}) => {}, // Getter intentionally empty as this is a write-only selector
    set: ({set, get}, {setbackAdded, segmentIndex}) => {
        // Only proceed if a valid segment index is provided
        if (segmentIndex !== null && segmentIndex !== undefined) {
            set(polygonsState, (oldPolygons) => {
                // Create a copy of the polygons array to maintain immutability
                const updatedPolygons = [...oldPolygons];

                // Update the setbacks of the specified segment
                updatedPolygons[segmentIndex] = {
                    ...updatedPolygons[segmentIndex],
                    setbacks: setbackAdded
                };

                return updatedPolygons;
            });
        } else {
            console.error('Invalid segmentIndex');
        }
    },
});